import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { Button } from '@/components/ui/button';
import { checkIsDev } from '@/lib/AuthSession';
import { Fingerprint, CheckCircle2, Clock } from 'lucide-react';
import AsaPopup from '../attendance/AsaPopup';
import FaceScanModal from '../attendance/FaceScanModal';
import { loadFaceApiModels } from '@/utils/faceApiLoader';

const FACTORY_LAT = -6.621211;
const FACTORY_LNG = 106.784465;
const MAX_DISTANCE_M = 150; // Increased to 150m for better indoor GPS accuracy

function getDistanceFromLatLonInM(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the earth in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 1000;
}

export default function ClockInButton() {
  const { member } = useSession();
  const queryClient = useQueryClient();
  const [todayStr, setTodayStr] = useState('');
  
  // Preload face-api models in background so modal opens instantly
  useEffect(() => {
    loadFaceApiModels();
  }, []);
  
  // Asa Popup State
  const [asaState, setAsaState] = useState({ isOpen: false, type: 'success', message: '' });
  
  // Face Scan State
  const [isFaceScanOpen, setIsFaceScanOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'check_in' | 'check_out'
  const [isLoadingGeo, setIsLoadingGeo] = useState(false);

  useEffect(() => {
    const tzOffset = (new Date()).getTimezoneOffset() * 60000;
    const localISOTime = (new Date(Date.now() - tzOffset)).toISOString().slice(0, 10);
    setTodayStr(localISOTime);
  }, []);

  const [workDuration, setWorkDuration] = useState('');

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["attendance-logs", todayStr, member?.id],
    queryFn: async () => {
      if (!todayStr || !member?.id) return [];
      const all = await base44.entities.AttendanceLog.list("-timestamp");
      return all.filter(log => 
        log.employee_id === member.id && 
        log.timestamp && 
        log.timestamp.startsWith(todayStr)
      );
    },
    enabled: !!todayStr && !!member?.id
  });

  const checkInMutation = useMutation({
    mutationFn: async ({ type, faceImage, selectedArea, lateReason, verificationResult }) => {
      const tzOffset = (new Date()).getTimezoneOffset() * 60000;
      const localISO = (new Date(Date.now() - tzOffset)).toISOString().slice(0, 19);
      
      let notesText = `Area: ${selectedArea}`;
      if (lateReason) {
        notesText += ` | Telat: ${lateReason}`;
      }

      let attendanceMethod = 'web_portal_face';
      let faceTag = '';

      if (verificationResult?.noFaceDetected) {
        attendanceMethod = 'web_portal_face_noface';
        faceTag = ' | ⚠️ AI: Wajah Tidak Terdeteksi';
      } else if (verificationResult?.hasReference === false) {
        attendanceMethod = 'web_portal_face_noref';
        faceTag = ' | ℹ️ AI: Belum Ada Acuan Wajah';
      } else if (typeof verificationResult?.distance === 'number') {
        const distFixed = verificationResult.distance.toFixed(2);
        if (verificationResult.isMatch) {
          attendanceMethod = 'web_portal_face';
          faceTag = ` | ✅ AI: Cocok (Jarak: ${distFixed})`;
        } else {
          attendanceMethod = 'web_portal_face_mismatch';
          faceTag = ` | ⚠️ AI: Wajah Beda (Jarak: ${distFixed})`;
        }
      }

      notesText += faceTag;

      let file_url = null;
      if (faceImage) {
        try {
          let arr = faceImage.split(','), mime = arr[0].match(/:(.*?);/)[1],
              bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
          while(n--){ u8arr[n] = bstr.charCodeAt(n); }
          const file = new File([u8arr], `absensi_${member.id}_${Date.now()}.jpg`, {type:mime});
          const res = await base44.integrations.Core.UploadFile({ file });
          file_url = res.file_url;
        } catch(e) {
          console.error("Gagal upload foto absensi", e);
        }
      }

      const newLog = {
        employee_id: member.id,
        full_name: member.full_name || member.username,
        timestamp: localISO,
        type: type,
        method: attendanceMethod,
        notes: notesText,
        photo_url: file_url // Menyimpan ke cloud storage
      };
      return await base44.entities.AttendanceLog.create(newLog);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["attendance-logs"]);
      determineAsaResponse(variables.type);
    },
    onError: (err) => {
      console.error(err);
      setAsaState({ isOpen: true, type: 'error', message: `Gagal menyimpan absen: ${err.message || 'Cek koneksi internet'}` });
    }
  });

  const determineAsaResponse = (type) => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const day = now.getDay(); // 0 = Sun, 5 = Fri
    const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    // Cek waktu istirahat
    const isFriday = day === 5;
    const breakStartHour = isFriday ? 11 : 12;
    const breakStartMin = isFriday ? 30 : 0;
    
    // Jika mereka absen tepat di waktu mulai istirahat
    if (hours === breakStartHour && minutes >= breakStartMin && minutes <= breakStartMin + 15) {
      setAsaState({ isOpen: true, type: 'break_start', message: '' });
      return;
    }
    // Jika mereka absen tepat jam selesai istirahat (13:00)
    if (hours === 13 && minutes <= 15) {
      setAsaState({ isOpen: true, type: 'break_end', message: '' });
      return;
    }

    if (type === 'check_in') {
      if (hours < 8) {
        setAsaState({ isOpen: true, type: 'success', message: `Kamu absen lebih cepat hari ini! Keren banget ${member.full_name || member.username}.` });
      } else if (hours === 8 && minutes <= 5) {
        setAsaState({ isOpen: true, type: 'success', message: `Tepat waktu! Selamat bekerja ${member.full_name || member.username}.` });
      } else {
        setAsaState({ isOpen: true, type: 'late', message: '' });
      }
    } else { // check_out
      if (hours < 17) {
        setAsaState({ isOpen: true, type: 'early_leave', message: '' });
      } else if (hours >= 17 && hours < 18) {
        setAsaState({ isOpen: true, type: 'regular_close', message: '' });
      } else {
        setAsaState({ isOpen: true, type: 'overtime', message: '' });
      }
    }
  };

  const startAttendance = (action) => {
    setPendingAction(action);
    setIsFaceScanOpen(true);
  };

  const handleFaceVerified = (base64Image, descriptor, selectedArea, lateReason, verificationResult) => {
    setIsFaceScanOpen(false);
    if (pendingAction) {
      checkInMutation.mutate({ 
        type: pendingAction, 
        faceImage: base64Image, 
        selectedArea, 
        lateReason,
        verificationResult 
      });
      setPendingAction(null);
    }
  };

  const checkInLog = logs.find(l => l.type === 'check_in');
  const checkOutLog = logs.find(l => l.type === 'check_out');
  const hasCheckedIn = !!checkInLog;
  const hasCheckedOut = !!checkOutLog;

  useEffect(() => {
    if (hasCheckedIn && !hasCheckedOut) {
      const interval = setInterval(() => {
        const diffMs = new Date() - new Date(checkInLog.timestamp);
        const hrs = Math.floor(diffMs / 3600000);
        const mins = Math.floor((diffMs % 3600000) / 60000);
        setWorkDuration(`${hrs}j ${mins}m`);
      }, 10000);
      
      const diffMs = new Date() - new Date(checkInLog.timestamp);
      const hrs = Math.floor(diffMs / 3600000);
      const mins = Math.floor((diffMs % 3600000) / 60000);
      setWorkDuration(`${hrs}j ${mins}m`);
      return () => clearInterval(interval);
    } else if (hasCheckedIn && hasCheckedOut) {
      const diffMs = new Date(checkOutLog.timestamp) - new Date(checkInLog.timestamp);
      const hrs = Math.floor(diffMs / 3600000);
      const mins = Math.floor((diffMs % 3600000) / 60000);
      setWorkDuration(`${hrs}j ${mins}m`);
    }
  }, [hasCheckedIn, hasCheckedOut, checkInLog, checkOutLog]);

  if (!member) return null;
  
  if (member.require_attendance === false) return null; 
  
  if (isLoading) return <Button disabled className="h-7 sm:h-8 px-2.5 sm:px-4 text-[10px] sm:text-xs bg-slate-100 text-slate-400"><span>Loading...</span></Button>;

  // Cek apakah sekarang sudah telat (di atas jam 08:05)
  const now = new Date();
  const isLate = !hasCheckedIn && (now.getHours() > 8 || (now.getHours() === 8 && now.getMinutes() > 5));

  const handleActionClick = () => {
    if (!hasCheckedIn) {
      startAttendance('check_in');
    } else if (!hasCheckedOut) {
      // Langsung absen pulang tanpa scan wajah
      checkInMutation.mutate({ 
        type: 'check_out', 
        faceImage: null, 
        selectedArea: 'Web Portal Checkout', 
        lateReason: null 
      });
    }
  };

  const isWorking = isLoadingGeo || checkInMutation.isPending;

  return (
    <>
      {hasCheckedOut ? (
        <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-500 rounded-lg text-xs font-bold border border-slate-200 opacity-70">
          <CheckCircle2 className="w-3.5 h-3.5 text-slate-400" />
          <span>Selesai Absen ({workDuration})</span>
        </div>
      ) : (
        <Button 
          onClick={handleActionClick}
          disabled={isWorking}
          className={`h-7 sm:h-8 px-2.5 sm:px-4 text-[10px] sm:text-xs font-black shadow-sm transition-all duration-300 ${
            hasCheckedIn 
              ? 'bg-rose-100 text-rose-700 hover:bg-rose-200 border border-rose-300 hover:shadow-rose-100'
              : isLate
                ? 'bg-amber-500 text-white hover:bg-amber-600 border-none hover:shadow-amber-200'
                : 'bg-emerald-500 text-white hover:bg-emerald-600 border-none hover:shadow-emerald-200'
          }`}
          title={isLate ? "Absen Terlambat" : "Absen via Web dengan GPS & Wajah"}
        >
          {isWorking ? (
            <span className="flex items-center gap-1.5">
              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
              <span>{isLoadingGeo ? 'Cek Lokasi...' : 'Memproses...'}</span>
            </span>
          ) : hasCheckedIn ? (
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> <span>Absen Pulang ({workDuration})</span>
            </span>
          ) : isLate ? (
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> <span>Absen (Terlambat)</span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <Fingerprint className="w-3.5 h-3.5" /> <span>Absen Sekarang</span>
            </span>
          )}
        </Button>
      )}

      <FaceScanModal 
        isOpen={isFaceScanOpen}
        onClose={() => {
          setIsFaceScanOpen(false);
          setPendingAction(null);
        }}
        onVerified={handleFaceVerified}
        referenceDescriptorStr={member.face_descriptor}
        isLate={isLate}
      />

      <AsaPopup 
        isOpen={asaState.isOpen}
        onClose={() => setAsaState({ ...asaState, isOpen: false })}
        type={asaState.type}
        userName={member.full_name || member.username}
        messageOverride={asaState.message}
      />
    </>
  );
}
